/**
 * Events API Service
 * Handles all API calls to the event-service via API Gateway
 */

import api from './api';

const EVENTS_BASE_URL = '/events';

/**
 * Extended timeout for large application-form / submission payloads only.
 * The default axios client timeout remains 30s (api.js) for all other requests.
 * Aligns with API gateway production proxy limit (60s) plus client headroom.
 */
export const EVENT_HEAVY_REQUEST_TIMEOUT_MS = 90000;
const eventHeavyRequest = { timeout: EVENT_HEAVY_REQUEST_TIMEOUT_MS };

// ============================================================================
// INDUSTRIES
// ============================================================================

export const getEventIndustries = (params = {}) => api.get(`${EVENTS_BASE_URL}/industries`, { params });
export const getEventIndustriesPicker = (params = {}) => api.get(`${EVENTS_BASE_URL}/industries/picker`, { params });
export const getEventIndustryById = (id) => api.get(`${EVENTS_BASE_URL}/industries/${id}`);
export const createEventIndustry = (payload) => api.post(`${EVENTS_BASE_URL}/industries`, payload);
export const updateEventIndustry = (id, payload) => api.put(`${EVENTS_BASE_URL}/industries/${id}`, payload);
export const deleteEventIndustry = (id) => api.delete(`${EVENTS_BASE_URL}/industries/${id}`);
export const bulkCreateEventIndustries = (payload) => api.post(`${EVENTS_BASE_URL}/industries/bulk`, payload);
export const bulkUpdateEventIndustries = (ids, isActive) => api.patch(`${EVENTS_BASE_URL}/industries/bulk`, { ids, isActive });
export const bulkDeleteEventIndustries = (ids) => api.delete(`${EVENTS_BASE_URL}/industries/bulk`, { data: { ids } });
export const exportEventIndustriesCsv = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/industries/export/csv`, { params, responseType: 'blob' });
export const exportEventIndustriesPdf = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/industries/export/pdf`, { params, responseType: 'blob' });

// ============================================================================
// CATEGORIES
// ============================================================================

export const getEventCategories = (params = {}) => api.get(`${EVENTS_BASE_URL}/categories`, { params });
export const getEventCategoriesPicker = (params = {}) => api.get(`${EVENTS_BASE_URL}/categories/picker`, { params });

/**
 * One page of the event form category picker.
 * @param {{ industryIds?: string[], eventTypeId: string, pricingMarkets?: string[], locationId?: string, page?: number, limit?: number, signal?: AbortSignal }} opts
 */
export const getEventCategoriesForEventPicker = ({
  industryIds,
  eventTypeId,
  pricingMarkets,
  locationId,
  page = 1,
  limit = 100,
  signal,
}) =>
  api.get(`${EVENTS_BASE_URL}/categories`, {
    params: {
      forEventPicker: true,
      page,
      limit,
      ...(industryIds?.length
        ? { industryIds: industryIds.map(String).filter(Boolean).join(',') }
        : {}),
      eventTypeId,
      ...(pricingMarkets?.length
        ? { pricingMarkets: pricingMarkets.map(String).filter(Boolean).join(',') }
        : {}),
      ...(locationId ? { locationId } : {}),
      isActive: 'true',
    },
    signal,
    timeout: 300000,
  });
export const getEventCategoriesTree = (industryId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/categories`, { params: { industryId, asTree: true, ...params } });
/** @deprecated Legacy full-tree load — card view uses lazy hierarchy endpoints below. */
export const getEventCategoriesHierarchy = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/categories`, {
    params: { ...params, hierarchyView: 'true' },
    timeout: 120000,
  });

/** Lazy card view: aggregate counts only (unlimited total records). */
export const getEventCategoriesHierarchySummary = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/categories/hierarchy/summary`, { params });

/** Lazy card view: paginated event types with category/industry counts. */
export const getEventCategoriesHierarchyEventTypes = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/categories/hierarchy/event-types`, { params });

/** Lazy card view: paginated industries for one event type. */
export const getEventCategoriesHierarchyIndustries = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/categories/hierarchy/industries`, { params });

/** Lazy card view: direct children of a parent category (paginated). */
export const getEventCategoriesHierarchyChildren = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/categories/hierarchy/children`, { params });

/** Lazy card view: flat paginated search results. */
export const getEventCategoriesHierarchySearch = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/categories/hierarchy/search`, { params });
export const getEventCategoryById = (id) => api.get(`${EVENTS_BASE_URL}/categories/${id}`);
export const createEventCategory = (payload) => api.post(`${EVENTS_BASE_URL}/categories`, payload);
export const bulkCreateEventCategories = (categories) => api.post(`${EVENTS_BASE_URL}/categories/bulk`, { categories });
export const updateEventCategory = (id, payload) => api.put(`${EVENTS_BASE_URL}/categories/${id}`, payload);
export const deleteEventCategory = (id) => api.delete(`${EVENTS_BASE_URL}/categories/${id}`);
export const bulkUpdateEventCategories = (ids, isActive) => api.patch(`${EVENTS_BASE_URL}/categories/bulk`, { ids, isActive });
export const bulkUpdateCategoryFinanceMapping = (ids, financeMapping) => api.patch(`${EVENTS_BASE_URL}/categories/bulk/finance-mapping`, { ids, financeMapping });
export const bulkDeleteEventCategories = (ids) => api.delete(`${EVENTS_BASE_URL}/categories/bulk`, { data: { ids } });
export const exportEventCategoriesCsv = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/categories/export/csv`, { params, responseType: 'blob' });
export const exportEventCategoriesPdf = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/categories/export/pdf`, { params, responseType: 'blob' });

// ============================================================================
// EVENT TYPES
// ============================================================================

export const getEventTypes = (params = {}) => api.get(`${EVENTS_BASE_URL}/event-types`, { params });
export const getEventTypesPicker = (params = {}) => api.get(`${EVENTS_BASE_URL}/event-types/picker`, { params });
export const getEventTypeById = (id) => api.get(`${EVENTS_BASE_URL}/event-types/${id}`);
export const createEventType = (payload) => api.post(`${EVENTS_BASE_URL}/event-types`, payload);
export const updateEventType = (id, payload) => api.put(`${EVENTS_BASE_URL}/event-types/${id}`, payload);
export const deleteEventType = (id) => api.delete(`${EVENTS_BASE_URL}/event-types/${id}`);
export const bulkUpdateEventTypes = (ids, isActive) => api.patch(`${EVENTS_BASE_URL}/event-types/bulk`, { ids, isActive });
export const bulkDeleteEventTypes = (ids) => api.delete(`${EVENTS_BASE_URL}/event-types/bulk`, { data: { ids } });

// ============================================================================
// JUDGES
// ============================================================================

export const getEventJudges = (params = {}) => api.get(`${EVENTS_BASE_URL}/judges`, { params });
export const getEventJudgeById = (id) => api.get(`${EVENTS_BASE_URL}/judges/${id}`);
export const getJudgePanelMemberships = (judgeId) =>
  api.get(`${EVENTS_BASE_URL}/judges/${judgeId}/panel-memberships`);
export const createEventJudge = (payload) => api.post(`${EVENTS_BASE_URL}/judges`, payload);
export const updateEventJudge = (id, payload) => api.put(`${EVENTS_BASE_URL}/judges/${id}`, payload);
export const deleteEventJudge = (id) => api.delete(`${EVENTS_BASE_URL}/judges/${id}`);
export const resetEventJudgePassword = (id, payload) => api.post(`${EVENTS_BASE_URL}/judges/${id}/reset-password`, payload);
export const getEventJudgePortalPassword = (id) => api.get(`${EVENTS_BASE_URL}/judges/${id}/portal-password`);
export const verifyEventJudgeLinkedEmail = (id) => api.post(`${EVENTS_BASE_URL}/judges/${id}/verify-linked-email`, {});
export const bulkUpdateEventJudges = (ids, isActive) => api.patch(`${EVENTS_BASE_URL}/judges/bulk`, { ids, isActive });
export const bulkDeleteEventJudges = (ids) => api.delete(`${EVENTS_BASE_URL}/judges/bulk`, { data: { ids } });
export const exportEventJudgesCsv = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/judges/export/csv`, { params, responseType: 'blob' });
export const exportEventJudgesPdf = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/judges/export/pdf`, { params, responseType: 'blob' });

// ── Auditors ─────────────────────────────────────────────────────────────────
export const getEventAuditors = (params = {}) => api.get(`${EVENTS_BASE_URL}/auditors`, { params });
export const getEventAuditorById = (id) => api.get(`${EVENTS_BASE_URL}/auditors/${id}`);
export const createEventAuditor = (payload) => api.post(`${EVENTS_BASE_URL}/auditors`, payload);
export const updateEventAuditor = (id, payload) => api.put(`${EVENTS_BASE_URL}/auditors/${id}`, payload);
export const deleteEventAuditor = (id) => api.delete(`${EVENTS_BASE_URL}/auditors/${id}`);
export const resetEventAuditorPassword = (id, payload) => api.post(`${EVENTS_BASE_URL}/auditors/${id}/reset-password`, payload);
export const verifyEventAuditorLinkedEmail = (id) => api.post(`${EVENTS_BASE_URL}/auditors/${id}/verify-linked-email`, {});
export const bulkUpdateEventAuditors = (ids, isActive) => api.patch(`${EVENTS_BASE_URL}/auditors/bulk`, { ids, isActive });
export const bulkDeleteEventAuditors = (ids) => api.delete(`${EVENTS_BASE_URL}/auditors/bulk`, { data: { ids } });
export const exportEventAuditorsCsv = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/auditors/export/csv`, { params, responseType: 'blob' });
export const exportEventAuditorsPdf = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/auditors/export/pdf`, { params, responseType: 'blob' });

// ============================================================================
// EXTERNAL COMPANIES / AGENCIES
// ============================================================================
export const getEventExternalCompanies = (params = {}) => api.get(`${EVENTS_BASE_URL}/external-companies`, { params });
export const getExternalCompanyCreditBalance = (companyId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/external-companies/${companyId}/credit-balance`, { params });
export const createEventExternalCompany = (payload) => api.post(`${EVENTS_BASE_URL}/external-companies`, payload);
export const updateEventExternalCompany = (id, payload) => api.put(`${EVENTS_BASE_URL}/external-companies/${id}`, payload);
export const deleteEventExternalCompany = (id) => api.delete(`${EVENTS_BASE_URL}/external-companies/${id}`);
export const resetEventExternalCompanyPassword = (id, payload) => api.post(`${EVENTS_BASE_URL}/external-companies/${id}/reset-password`, payload);
export const verifyEventExternalCompanyLinkedEmail = (id) =>
  api.post(`${EVENTS_BASE_URL}/external-companies/${id}/verify-linked-email`, {});
export const updateEventExternalCompaniesBulkStatus = (ids, isActive) =>
  api.patch(`${EVENTS_BASE_URL}/external-companies/bulk`, { ids, isActive });
export const deleteEventExternalCompaniesBulk = (ids) =>
  api.delete(`${EVENTS_BASE_URL}/external-companies/bulk`, { data: { ids } });
export const exportEventExternalCompaniesCsv = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/external-companies/export/csv`, { params, responseType: 'blob' });
export const exportEventExternalCompaniesPdf = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/external-companies/export/pdf`, { params, responseType: 'blob' });
export const getExternalCompanyClients = (id) =>
  api.get(`${EVENTS_BASE_URL}/external-companies/${id}/clients`);
export const addExternalCompanyClient = (id, payload) =>
  api.post(`${EVENTS_BASE_URL}/external-companies/${id}/clients`, payload);
export const updateExternalCompanyClient = (id, clientId, payload) =>
  api.put(`${EVENTS_BASE_URL}/external-companies/${id}/clients/${clientId}`, payload);
export const deleteExternalCompanyClient = (id, clientId) =>
  api.delete(`${EVENTS_BASE_URL}/external-companies/${id}/clients/${clientId}`);

// ============================================================================
// EVENTS
// ============================================================================

export const getEvents = (params = {}) => api.get(`${EVENTS_BASE_URL}/events`, { params });
export const exportEventsCsv = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/export/csv`, { params, responseType: 'blob' });
export const exportEventsPdf = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/export/pdf`, { params, responseType: 'blob' });
export const getEventById = (id, params = {}) => api.get(`${EVENTS_BASE_URL}/events/${id}`, { params });
/** Lightweight event fields for application-form ↔ event linking (no full panel populate). */
export const getEventFormLinkContext = (id) => api.get(`${EVENTS_BASE_URL}/events/${id}/form-link-context`);
export const getEventByApplicationForm = (formId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/by-application-form/${formId}`, { params });
export const createEvent = (payload) => api.post(`${EVENTS_BASE_URL}/events`, payload);
export const updateEvent = (id, payload) => api.put(`${EVENTS_BASE_URL}/events/${id}`, payload);
export const updateEventExternalPortal = (id, payload) => api.patch(`${EVENTS_BASE_URL}/events/${id}/external-portal`, payload);
export const updateEventBulkDiscounts = (id, payload) => api.put(`${EVENTS_BASE_URL}/events/${id}/bulk-discounts`, payload);
export const getEventPromoCodes = (eventId) => api.get(`${EVENTS_BASE_URL}/events/${eventId}/promo-codes`);
export const createEventPromoCode = (eventId, payload) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/promo-codes`, payload);
export const updateEventPromoCode = (eventId, promoId, payload) => api.put(`${EVENTS_BASE_URL}/events/${eventId}/promo-codes/${promoId}`, payload);
export const deleteEventPromoCode = (eventId, promoId) => api.delete(`${EVENTS_BASE_URL}/events/${eventId}/promo-codes/${promoId}`);
export const validateEventPromoCode = (eventId, payload) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/promo-codes/validate`, payload);
export const updateEventJudgingCriteria = (id, payload) =>
  api.put(`${EVENTS_BASE_URL}/events/${id}/judging-criteria`, payload);
/** Skip screening: preview (EVENTS:SKIP_SCREENING:READ) or advance (EVENTS:SKIP_SCREENING:ADVANCE). */
export const previewAdvanceSubmittedToJudging = (id) =>
  api.post(
    `${EVENTS_BASE_URL}/events/${id}/advance-submitted-to-judging`,
    { dryRun: true },
    { params: { dryRun: 'true' }, skipErrorToast: true }
  );
export const advanceSubmittedToJudging = (id, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/events/${id}/advance-submitted-to-judging`, { dryRun: false, ...payload });
/** Skip screening (manual): search applications to hand-pick. */
/** Skip screening (manual): paged, searchable options for the company / category / industry filters. */
export const searchSkipScreeningFilterOptions = (id, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${id}/skip-screening/filter-options`, { params, skipErrorToast: true });
export const searchSkipScreeningApplications = (id, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${id}/skip-screening/applications`, { params, skipErrorToast: true });
/** Skip screening (manual): dry-run impact of advancing exactly these applications. */
export const previewAdvanceSelectedToJudging = (id, submissionIds) =>
  api.post(
    `${EVENTS_BASE_URL}/events/${id}/advance-submitted-to-judging`,
    { dryRun: true, submissionIds },
    { params: { dryRun: 'true' }, skipErrorToast: true }
  );
export const deleteEvent = (id, options = {}) =>
  api.delete(`${EVENTS_BASE_URL}/events/${id}`, {
    params: options.force ? { force: 'true' } : {},
  });
export const preflightApproveEvent = (id) => api.get(`${EVENTS_BASE_URL}/events/${id}/approval-preflight`);
export const approveEvent = (id, payload) => api.post(`${EVENTS_BASE_URL}/events/${id}/approve`, payload);
export const rejectEvent = (id, payload) => api.post(`${EVENTS_BASE_URL}/events/${id}/reject`, payload);
export const submitEventDraft = (id) => api.post(`${EVENTS_BASE_URL}/events/${id}/submit`);
export const deactivateEvent = (id) => api.post(`${EVENTS_BASE_URL}/events/${id}/deactivate`, {});
export const reactivateEvent = (id) => api.post(`${EVENTS_BASE_URL}/events/${id}/reactivate`, {});

// ============================================================================
// APPLICATION FORMS
// ============================================================================

export const getApplicationForms = (params = {}) => api.get(`${EVENTS_BASE_URL}/application-forms`, { params });
export const exportApplicationFormsCsv = (params = {}) => api.get(`${EVENTS_BASE_URL}/application-forms/export/csv`, { params, responseType: 'blob' });
export const exportApplicationFormsPdf = (params = {}) => api.get(`${EVENTS_BASE_URL}/application-forms/export/pdf`, { params, responseType: 'blob' });
export const getApplicationFormById = (id) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/${id}`, eventHeavyRequest);
export const getApplicationFormByToken = (token) => api.get(`${EVENTS_BASE_URL}/application-forms/public/${token}`);
/** Authenticated form load for the external-company portal apply flow (no public form access). */
export const getCoordinatorApplicationFormByToken = (token) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/form`);
export const validateFormPromoCode = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/validate-promo`, payload)
    .catch(() => api.post(`${EVENTS_BASE_URL}/application-forms/by-token/${token}/validate-promo`, payload));
export const getApplicationFormCompanyVolumeStats = (token, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-token/${token}/company-volume-stats`, { params });
export const getCoordinatorApplicationFormVolumeStats = (token, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/volume-stats`, { params });
export const getPublicApplicationFormExternalCompanies = (token, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/public/${token}/external-companies`, { params });
export const getPublicApplicationFormCompanyClients = (token, companyId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/public/${token}/external-companies/${companyId}/clients`);
export const addPublicApplicationFormCompanyClient = (token, companyId, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/by-token/${token}/external-companies/${companyId}/clients`, payload);
export const getApplicationFormTemplates = (params = {}) => api.get(`${EVENTS_BASE_URL}/application-forms/templates`, { params });
export const createApplicationFormTemplate = (payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/templates`, payload, eventHeavyRequest);
export const updateApplicationFormTemplate = (id, payload) =>
  api.put(`${EVENTS_BASE_URL}/application-forms/templates/${id}`, payload, eventHeavyRequest);
export const getEventEligibilitiesForBuilder = (eventId, pricingMarket) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/event-eligibilities`, {
    params: { eventId, ...(pricingMarket ? { pricingMarket } : {}) },
  });
export const deleteApplicationFormTemplate = (id) => api.delete(`${EVENTS_BASE_URL}/application-forms/templates/${id}`);
export const bulkDeleteApplicationFormTemplates = (ids) => api.post(`${EVENTS_BASE_URL}/application-forms/templates/bulk-delete`, { ids });
export const previewApplicationFormByToken = (token) => api.get(`${EVENTS_BASE_URL}/application-forms/preview/by-token/${token}`);
export const getPreviewApplicationFormExternalCompanies = (token, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/preview/by-token/${token}/external-companies`, { params });
export const getPreviewApplicationFormCompanyClients = (token, companyId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/preview/by-token/${token}/external-companies/${companyId}/clients`);
export const createApplicationForm = (payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms`, payload, eventHeavyRequest);
export const updateApplicationForm = (id, payload) =>
  api.put(`${EVENTS_BASE_URL}/application-forms/${id}`, payload, eventHeavyRequest);
export const updateApprovedApplicationFormDetails = (id, payload) =>
  api.patch(`${EVENTS_BASE_URL}/application-forms/${id}/approved-details`, payload);
export const deleteApplicationForm = (id) => api.delete(`${EVENTS_BASE_URL}/application-forms/${id}`);
export const approveApplicationForm = (id, payload = {}) => api.post(`${EVENTS_BASE_URL}/application-forms/${id}/approve`, payload);
export const rejectApplicationForm = (id, payload = {}) => api.post(`${EVENTS_BASE_URL}/application-forms/${id}/reject`, payload);
export const submitPublicApplicationForm = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/public/${token}/submit`, payload, eventHeavyRequest);

/** Staff: submit an application on behalf of an external company (requires EVENTS:SUBMISSIONS:CREATE). */
export const getStaffApplicationFormByToken = (token) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/staff/by-token/${token}/form`);
export const getStaffApplicationFormExternalCompanies = (token, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/staff/by-token/${token}/external-companies`, { params });
export const getStaffApplicationFormCompanyClients = (token, companyId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/staff/by-token/${token}/external-companies/${companyId}/clients`);
export const staffSubmitApplicationFormOnBehalf = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/staff/by-token/${token}/submit`, payload, eventHeavyRequest);
export const downloadPublicSubmissionPdf = (submissionId, packKey) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/public/submissions/${submissionId}/pdf`, {
    responseType: 'blob',
    params: packKey ? { pack: packKey } : undefined,
  });
export const listPublicSubmissionPdfPacks = (submissionId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/public/submissions/${submissionId}/pdf-packs`);
export const downloadSubmissionPdf = (submissionId, packKey) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/submissions/${submissionId}/pdf`, {
    responseType: 'blob',
    params: packKey ? { pack: packKey } : undefined,
  });
export const listSubmissionPdfPacks = (submissionId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/submissions/${submissionId}/pdf-packs`);
export const emailStaffSubmissionPdf = (submissionId, data = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/submissions/${submissionId}/email-pdf`, data);
export const downloadCoordinatorSubmissionPdf = (submissionId, config = {}) => {
  const { packKey, ...rest } = config || {};
  return api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/submissions/${submissionId}/pdf`, {
    responseType: 'blob',
    params: packKey ? { pack: packKey } : undefined,
    ...rest,
    skipErrorToast: rest.skipErrorToast ?? true,
  });
};
export const listCoordinatorSubmissionPdfPacks = (submissionId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/submissions/${submissionId}/pdf-packs`);
export const emailCoordinatorSubmissionPdf = (submissionId) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/coordinator/submissions/${submissionId}/email-pdf`);

/** Logged-in coordinator / external company (requires EVENTS:EXTERNAL_PORTAL:ACCESS) */
export const getCoordinatorMyExternalCompany = () =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/my-external-company`);
export const getCoordinatorMyCompanyClients = () =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/my-company-clients`);
export const addCoordinatorMyCompanyClient = (payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/coordinator/my-company-clients`, payload);
export const coordinatorSubmitApplicationForm = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/submit`, payload, eventHeavyRequest);
export const saveCoordinatorApplicationDraft = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/drafts`, payload, eventHeavyRequest);
export const updateCoordinatorApplicationDraft = (token, draftId, payload) =>
  api.put(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/drafts/${draftId}`, payload, eventHeavyRequest);
export const getCoordinatorApplicationDraft = (token, draftId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/drafts/${draftId}`);
export const deleteCoordinatorApplicationDraft = (token, draftId) =>
  api.delete(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/drafts/${draftId}`);
export const listCoordinatorApplicationDrafts = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/drafts`, { params });
export const getCoordinatorMyEvents = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/my-events`, { params });
export const exportCoordinatorMyEventsCsv = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/my-events/export/csv`, { params, responseType: 'blob' });
export const exportCoordinatorMyEventsPdf = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/my-events/export/pdf`, { params, responseType: 'blob' });
export const getCoordinatorMyApplicationSubmissions = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/my-submissions`, { params });
export const getCoordinatorInvitedForms = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/invited-forms`, { params });
export const exportCoordinatorMyApplicationSubmissions = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/my-submissions/export`, { params, responseType: 'blob' });
export const exportCoordinatorMyApplicationSubmissionsPdf = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/my-submissions/export/pdf`, { params, responseType: 'blob' });
export const getCoordinatorApplicationSubmission = (token, submissionId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/submissions/${submissionId}`);
export const updateCoordinatorApplicationSubmission = (token, submissionId, payload) =>
  api.put(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/submissions/${submissionId}`, payload, eventHeavyRequest);
export const deleteCoordinatorApplicationSubmission = (token, submissionId, options = {}) =>
  api.delete(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/submissions/${submissionId}`, {
    data: options,
  });
export const listCoordinatorProformaEligibleSubmissions = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/proforma-eligible-submissions`, { params });
export const getCoordinatorEventFinancialSummary = (eventId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/event-financial-summary`, { params: { eventId } });
export const createCoordinatorProformaRequest = (payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/coordinator/proforma-requests`, payload, eventHeavyRequest);
export const listCoordinatorProformaRequests = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/proforma-requests`, { params });
export const downloadCoordinatorPerformaInvoicePdf = (requestId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/proforma-requests/${requestId}/invoice/pdf`, {
    responseType: 'blob',
  });
export const downloadCoordinatorTaxInvoicePdf = (requestId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/proforma-requests/${requestId}/tax-invoice/pdf`, {
    responseType: 'blob',
  });
export const requestCoordinatorProformaPaymentLink = (requestId) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/coordinator/proforma-requests/${requestId}/request-payment-link`, {});
export const downloadCoordinatorBillingInvoicePdf = (requestId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/proforma-requests/${requestId}/billing-invoice/pdf`, {
    responseType: 'blob',
  });
export const requestCoordinatorBillingInvoice = (requestId) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/coordinator/proforma-requests/${requestId}/request-billing-invoice`, {});
export const requestCoordinatorTaxInvoice = (requestId) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/coordinator/proforma-requests/${requestId}/request-tax-invoice`, {});
export const cancelCoordinatorProformaRequest = (requestId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/coordinator/proforma-requests/${requestId}/cancel`, payload);
export const getCoordinatorProformaPaymentLink = (requestId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/proforma-requests/${requestId}/payment-link`);
export const getPublicProformaPayment = (token) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/public/proforma-payments/${token}`);
export const downloadPublicProformaTaxInvoicePdf = (token) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/public/proforma-payments/${token}/tax-invoice/pdf`, { responseType: 'blob' });
export const downloadPublicProformaBillingInvoicePdf = (token) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/public/proforma-payments/${token}/billing-invoice/pdf`, { responseType: 'blob' });
export const initiatePublicProformaCardPayment = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/public/proforma-payments/${token}/initiate-card`, payload);
export const submitPublicProformaOfflinePayment = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/public/proforma-payments/${token}/submit-offline`, payload);
export const submitPublicProformaSignedDocumentsResubmission = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/public/proforma-payments/${token}/resubmit-signed-documents`, payload);
export const listScreeningPhase1Submissions = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/screening/phase-1/submissions`, { params });
export const listScreeningPhase2Submissions = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/screening/phase-2/submissions`, { params });
export const listScreeningPhase1Events = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/screening/phase-1/events`, { params });
export const listScreeningPhase2Events = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/screening/phase-2/events`, { params });
export const exportScreeningPhase1SubmissionsCsv = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/screening/phase-1/submissions/export/csv`, { params, responseType: 'blob' });
export const exportScreeningPhase1SubmissionsPdf = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/screening/phase-1/submissions/export/pdf`, { params, responseType: 'blob' });
export const exportScreeningPhase2SubmissionsCsv = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/screening/phase-2/submissions/export/csv`, { params, responseType: 'blob' });
export const exportScreeningPhase2SubmissionsPdf = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/screening/phase-2/submissions/export/pdf`, { params, responseType: 'blob' });
export const approveScreeningPhase1Submission = (submissionId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/screening/phase-1/submissions/${submissionId}/approve`, payload);
export const rejectScreeningPhase1Submission = (submissionId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/screening/phase-1/submissions/${submissionId}/reject`, payload);
export const approveScreeningPhase2Submission = (submissionId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/screening/phase-2/submissions/${submissionId}/approve`, payload);
export const rejectScreeningPhase2Submission = (submissionId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/screening/phase-2/submissions/${submissionId}/reject`, payload);
export const holdScreeningPhase1Submission = (submissionId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/screening/phase-1/submissions/${submissionId}/hold`, payload);
export const releaseScreeningPhase1Hold = (submissionId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/screening/phase-1/submissions/${submissionId}/release-hold`, payload);
export const holdScreeningPhase2Submission = (submissionId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/screening/phase-2/submissions/${submissionId}/hold`, payload);
export const releaseScreeningPhase2Hold = (submissionId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/screening/phase-2/submissions/${submissionId}/release-hold`, payload);
export const bulkScreeningPhase1Action = (payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/screening/phase-1/submissions/bulk-action`, payload, eventHeavyRequest);
export const bulkScreeningPhase2Action = (payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/screening/phase-2/submissions/bulk-action`, payload, eventHeavyRequest);
export const requestSubmissionExtraDocument = (submissionId, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/screening/phase-1/submissions/${submissionId}/extra-document-request`, payload);

const screeningSignedDocPath = (phase, submissionId, action) =>
  `${EVENTS_BASE_URL}/application-forms/screening/phase-${phase}/submissions/${submissionId}/${action}`;

export const requestScreeningSignedDocument = (submissionId, payload, phase = 1) =>
  api.post(screeningSignedDocPath(phase, submissionId, 'request-signed-document'), payload);
export const verifyScreeningSignedDocument = (submissionId, payload, phase = 1) =>
  api.post(screeningSignedDocPath(phase, submissionId, 'verify-signed-document'), payload);
export const rejectScreeningSignedDocument = (submissionId, payload, phase = 1) =>
  api.post(screeningSignedDocPath(phase, submissionId, 'reject-signed-document'), payload);
export const requestScreeningSignedDocumentResubmit = (submissionId, payload, phase = 1) =>
  api.post(screeningSignedDocPath(phase, submissionId, 'request-signed-document-resubmit'), payload);
export const uploadCoordinatorExtraDocument = (token, submissionId, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/submissions/${submissionId}/extra-document`, payload);
export const uploadCoordinatorSignedApplicationDocument = (token, submissionId, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/submissions/${submissionId}/signed-application-document`, payload);
export const uploadPublicProformaExtraDocument = (paymentToken, submissionId, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/public/proforma-payments/${paymentToken}/submissions/${submissionId}/extra-document`, payload);
export const savePublicApplicationDraft = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/public/${token}/drafts`, payload);
export const updatePublicApplicationDraft = (token, draftToken, payload) =>
  api.put(`${EVENTS_BASE_URL}/application-forms/public/${token}/drafts/${draftToken}`, payload);
export const submitPublicPaymentReferenceForApproval = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/public/${token}/payment-reference`, payload);
export const getRegistrationPaymentStatus = (token, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/public/${token}/registration-payment-status`, { params });
export const initiateRegistrationPayment = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/public/${token}/initiate-registration-payment`, payload);
export const getApplicationFormSubmissions = (id, params = {}) => api.get(`${EVENTS_BASE_URL}/application-forms/${id}/submissions`, { params });

/** Submissions for the application form linked to an event (same filters as form submissions). */
export const getEventApplicationFormSubmissions = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions`, { params });

/** Distinct external companies with submissions for this event (APPLICATIONFORMS:READ). */
export const getEventSubmissionExternalCompanies = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/external-companies`, { params });

/** Distinct categories present in this event's submissions — drives the category filter dropdown on the Submissions tab. */
export const getEventSubmissionCategories = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/categories`, { params });

/** Distinct industries present in this event's submissions — drives the industry filter when category–industry linking is on. */
export const getEventSubmissionIndustries = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/industries`, { params });
/** Distinct external companies/agencies with proforma requests for this event. */
export const getEventProformaRequestExternalCompanies = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/external-companies`, { params });
export const getEventProformaInvoiceRequests = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests`, { params });
export const cancelEventProformaRequest = (eventId, requestId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/${requestId}/cancel`, payload);
export const splitEventProformaInvoiceRequest = (eventId, requestId, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/${requestId}/split`, payload);
export const staffRequestTaxInvoiceForProforma = (eventId, requestId) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/${requestId}/staff-request-tax-invoice`, {});
export const staffListCompanyProformaEligibleSubmissions = (eventId, companyId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/staff/external-companies/${companyId}/proforma-eligible-submissions`);
export const staffCreateProformaRequest = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/staff/proforma-requests`, payload, eventHeavyRequest);
export const exportEventProformaRequestsCsv = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/export/csv`, {
    params,
    responseType: 'blob',
  });
export const exportEventProformaRequestsPdf = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/export/pdf`, {
    params,
    responseType: 'blob',
  });
export const getEventProformaPayments = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-payments`, { params });
export const getLegacyOnboardingCandidates = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/legacy-onboarding/candidates`, { params });
export const getLegacyOnboardingOpenRequests = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/legacy-onboarding/open-requests`, { params });
export const getLegacyOnboardingBatch = (eventId, batchId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/legacy-onboarding/${encodeURIComponent(batchId)}`, { params });
export const createLegacyOnboardingDraftBatch = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/legacy-onboarding/draft-batch`, payload);
export const reconcileLegacyOnboardingBatch = (eventId, batchId, payload = {}) =>
  batchId
    ? api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/legacy-onboarding/${encodeURIComponent(batchId)}/reconcile`, payload)
    : api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/legacy-onboarding/reconcile`, payload);
export const issueLegacyOnboardingBatchInvoices = (eventId, batchId, payload) =>
  batchId
    ? api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/legacy-onboarding/${encodeURIComponent(batchId)}/issue-invoices`, payload)
    : api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/legacy-onboarding/issue-invoices`, payload);
export const approveLegacyOnboardingBatchPayments = (eventId, batchId, payload = {}) =>
  batchId
    ? api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/legacy-onboarding/${encodeURIComponent(batchId)}/approve-payments`, payload)
    : api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/legacy-onboarding/approve-payments`, payload);
export const finalizeLegacyOnboardingBatch = (eventId, batchId, payload = {}) =>
  batchId
    ? api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/legacy-onboarding/${encodeURIComponent(batchId)}/finalize`, payload)
    : api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/legacy-onboarding/finalize`, payload);
export const exportEventProformaPaymentsCsv = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-payments/export/csv`, {
    params,
    responseType: 'blob',
  });
export const exportEventProformaPaymentsPdf = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-payments/export/pdf`, {
    params,
    responseType: 'blob',
  });
export const approveProformaPayment = (requestId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/proforma-requests/${requestId}/payments/approve`, payload);
export const rejectProformaPayment = (requestId, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/proforma-requests/${requestId}/payments/reject`, payload);
export const bulkApproveEventProformaPayments = (eventId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-payments/bulk-approve`, payload);
export const bulkRejectEventProformaPayments = (eventId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-payments/bulk-reject`, payload);

/** Cross-event Manual Payments against issued invoices / tax invoices */
export const listEventManualPaymentInvoices = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/manual-payment-invoices`, { params });
export const getEventManualPaymentContext = (requestId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/proforma-requests/${requestId}/manual-payment`);
export const recordEventStaffProformaManualPayment = (requestId, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/proforma-requests/${requestId}/manual-payment`, payload);

export const requestProformaSignedDocumentResubmission = (requestId, payload = {}) =>
  api.post(
    `${EVENTS_BASE_URL}/application-forms/proforma-requests/${requestId}/signed-documents/request-resubmission`,
    payload
  );

export const generateEventPerformaInvoice = (eventId, requestId, payload = {}) =>
  api.post(
    `${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/${requestId}/invoice`,
    payload
  );

export const downloadEventPerformaInvoicePdf = (eventId, requestId) =>
  api.get(
    `${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/${requestId}/invoice/pdf`,
    { responseType: 'blob' }
  );

export const emailEventPerformaInvoice = (eventId, requestId) =>
  api.post(
    `${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/${requestId}/invoice/email`,
    {}
  );

export const issueEventBillingInvoice = (eventId, requestId, payload = {}) =>
  api.post(
    `${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/${requestId}/billing-invoice`,
    payload
  );
export const rejectEventBillingInvoiceRequest = (eventId, requestId, payload = {}) =>
  api.post(
    `${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/${requestId}/billing-invoice/reject`,
    payload
  );
export const downloadEventBillingInvoicePdf = (eventId, requestId, copy = 'duplicate') =>
  api.get(
    `${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/${requestId}/billing-invoice/pdf`,
    { responseType: 'blob', params: { copy, _: Date.now() } }
  );
export const issueEventTaxInvoice = (eventId, requestId, payload = {}) =>
  api.post(
    `${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/${requestId}/tax-invoice`,
    payload
  );

export const rejectEventTaxInvoiceRequest = (eventId, requestId, payload = {}) =>
  api.post(
    `${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/${requestId}/tax-invoice/reject`,
    payload
  );

export const downloadEventTaxInvoicePdf = (eventId, requestId, copy = 'duplicate') =>
  api.get(
    `${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/${requestId}/tax-invoice/pdf`,
    { responseType: 'blob', params: { copy, _: Date.now() } }
  );

export const downloadStaffProformaPaymentReceiptPdf = (eventId, requestId, paymentKey) =>
  api.get(
    `${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/${requestId}/receipts/${encodeURIComponent(paymentKey)}/pdf`,
    { responseType: 'blob', params: { _: Date.now() } }
  );

export const emailEventProformaPaymentReceipt = (eventId, requestId, paymentKey) =>
  api.post(
    `${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/proforma-invoice-requests/${requestId}/receipts/${encodeURIComponent(paymentKey)}/email`
  );

export const downloadCoordinatorProformaPaymentReceiptPdf = (requestId, paymentKey = 'latest') =>
  api.get(
    `${EVENTS_BASE_URL}/application-forms/coordinator/proforma-requests/${requestId}/receipts/${encodeURIComponent(paymentKey)}/pdf`,
    { responseType: 'blob', params: { _: Date.now() } }
  );

export const downloadPublicProformaPaymentReceiptPdf = (token, paymentKey = 'latest') =>
  api.get(
    `${EVENTS_BASE_URL}/application-forms/public/proforma-payments/${token}/receipts/${encodeURIComponent(paymentKey)}/pdf`,
    { responseType: 'blob', params: { _: Date.now() } }
  );

export const getEventApplicationFormSubmissionDetail = (eventId, submissionId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/${submissionId}`);

export const getEventApplicationFormSubmissionForEdit = (eventId, submissionId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/${submissionId}/edit`);

export const updateEventApplicationFormSubmission = (eventId, submissionId, payload) =>
  api.put(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/${submissionId}`, payload, eventHeavyRequest);

export const uploadStaffSignedApplicationDocument = (eventId, submissionId, payload) =>
  api.post(
    `${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/${submissionId}/signed-application-document`,
    payload
  );

export const downloadStaffSubmissionVersionPdf = (eventId, submissionId, versionId, packKey) =>
  api.get(
    `${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/${submissionId}/versions/${versionId}/pdf`,
    {
      responseType: 'blob',
      params: {
        _: Date.now(),
        ...(packKey ? { pack: packKey } : {}),
      },
    }
  );

export const listStaffSubmissionVersions = (eventId, submissionId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/${submissionId}/versions`);
export const getStaffSubmissionVersionDetail = (eventId, submissionId, versionId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/${submissionId}/versions/${versionId}`);

export const listCoordinatorSubmissionVersions = (token, submissionId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/submissions/${submissionId}/versions`);
export const getCoordinatorSubmissionVersionDetail = (token, submissionId, versionId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/submissions/${submissionId}/versions/${versionId}`);
export const downloadCoordinatorSubmissionVersionPdf = (token, submissionId, versionId, packKey) =>
  api.get(
    `${EVENTS_BASE_URL}/application-forms/coordinator/by-token/${token}/submissions/${submissionId}/versions/${versionId}/pdf`,
    {
      responseType: 'blob',
      params: {
        _: Date.now(),
        ...(packKey ? { pack: packKey } : {}),
      },
    }
  );

export const exportEventApplicationFormSubmissionsCsv = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/export/csv`, { params, responseType: 'blob' });

export const exportEventApplicationFormSubmissionsPdf = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/export/pdf`, { params, responseType: 'blob' });

export const getApplicationFormPaymentReferences = (id, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/${id}/payment-references`, { params });

export const getApplicationFormRegistrationPayments = (id, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/${id}/registration-payments`, { params });

export const approveApplicationFormPaymentReference = (formId, paymentId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/${formId}/payment-references/${paymentId}/approve`, payload);

export const rejectApplicationFormPaymentReference = (formId, paymentId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/${formId}/payment-references/${paymentId}/reject`, payload);

export const acceptApplicationFormSubmission = (formId, submissionId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/${formId}/submissions/${submissionId}/accept`, payload);

export const rejectApplicationFormSubmission = (formId, submissionId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/${formId}/submissions/${submissionId}/reject`, payload);

export const deleteEventApplicationFormSubmission = (eventId, submissionId, options = {}) =>
  api.delete(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/${submissionId}`, {
    data: options,
  });

export const bulkDeleteEventApplicationFormSubmissions = (eventId, submissionIds, options = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/bulk-delete`, {
    submissionIds,
    ...options,
  }, eventHeavyRequest);

export const previewEventSubmissionStagePricing = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/stage-pricing/preview`, payload, eventHeavyRequest);

export const applyEventSubmissionStagePricing = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/stage-pricing/apply`, payload, eventHeavyRequest);

export const waitlistEventSubmission =(eventId, submissionId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/${submissionId}/waitlist`, payload);

export const archiveEventSubmission = (eventId, submissionId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/submissions/${submissionId}/archive`, payload);

export const activateWaitlistedEventSubmissions = (eventId, payload = {}, params = {}) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/by-event/${eventId}/waitlist/activate`, payload, { params });

export const assignApplicationFormSubmissionJudgePanel = (formId, submissionId, payload) =>
  api.post(
    `${EVENTS_BASE_URL}/application-forms/${formId}/submissions/${submissionId}/assign-judge-panel`,
    payload
  );

// ============================================================================
// INVITATIONS
// ============================================================================

// Uses event-service external-company/agency records (already synced from auth-service).
export const getExternalCompanyUsers = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/external-companies`, { params });

// Invite brand-new external companies/agencies to register (sign up) on the system.
// payload: { signupUrl, expiryDays?, recipients: [{ email, name? }] }
export const sendRegistrationInvitations = (payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/registration-invitations`, payload);

// Invite existing external companies/agencies to submit applications for a specific form.
// payload: { publicLink, recipients?: [{ email, name? }], sendToAll?, confirmAll?, companyType? }
export const sendApplicationFormInvitations = (formId, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/${formId}/invitations`, payload);

/** Public — validate a time-limited registration invite token (sign-up page). */
export const validateRegistrationInvitation = (token) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/public/registration-invitations/${encodeURIComponent(token)}`);

/** Public — mark a registration invite as used after successful sign-up. */
export const consumeRegistrationInvitation = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/public/registration-invitations/${encodeURIComponent(token)}/consume`, payload);

// ============================================================================
// SETTINGS
// ============================================================================

/** Public — no auth token required. Used by the sign-up page. */
export const getPublicEventSettings = () =>
  api.get(`${EVENTS_BASE_URL}/settings/public`);

/** Protected — requires EVENTS:SETTINGS:READ */
export const getEventSettings = () =>
  api.get(`${EVENTS_BASE_URL}/settings`);

/** Protected — requires EVENTS:SETTINGS:UPDATE */
export const updateEventSettings = (payload) =>
  api.put(`${EVENTS_BASE_URL}/settings`, payload);

/** Shared judging criteria template presets (all events). Requires EVENTS:CRITERIA:READ|UPDATE */
export const getCriteriaTemplatePresets = () =>
  api.get(`${EVENTS_BASE_URL}/criteria-template-presets`);

/** Upsert one custom preset by name. Requires EVENTS:CRITERIA:UPDATE */
export const saveCriteriaTemplatePreset = (payload) =>
  api.post(`${EVENTS_BASE_URL}/criteria-template-presets`, payload);

/** Delete one custom preset by name. Requires EVENTS:CRITERIA:UPDATE */
export const deleteCriteriaTemplatePreset = (name) =>
  api.delete(`${EVENTS_BASE_URL}/criteria-template-presets/${encodeURIComponent(name)}`);

/** Protected — requires ADMIN:SIGNUP_SETTINGS:READ */
export const getSignUpPageSettings = () =>
  api.get(`${EVENTS_BASE_URL}/settings/signup-page`);

/** Protected — requires ADMIN:SIGNUP_SETTINGS:UPDATE */
export const updateSignUpPageSettings = (payload) =>
  api.put(`${EVENTS_BASE_URL}/settings/signup-page`, payload);

// ============================================================================
// DASHBOARD STATS
// ============================================================================
export const getEventDashboardAnalytics = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/dashboard/analytics`, { params });

export const getEventDashboardStats = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/dashboard/stats`, { params });

// ============================================================================
// EVENT PANELS (event-scoped judge panels)
// ============================================================================

export const getEventPanels = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/panels`, { params });
export const getEventPanelCategoryOverview = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/panels/category-overview`, { params });
export const createEventPanel = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/panels`, payload);
export const updateEventPanel = (eventId, panelId, payload) =>
  api.put(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}`, payload);
export const deleteEventPanel = (eventId, panelId) =>
  api.delete(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}`);
export const assignEventPanelToCategory = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/panels/assign`, payload);
export const bulkAssignEventPanelToCategories = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/panels/assign/bulk`, payload);
export const unassignEventPanelFromCategory = (eventId, categoryId) =>
  api.delete(`${EVENTS_BASE_URL}/events/${eventId}/panels/assign/${categoryId}`);
export const setEventPanelJuryHeadLive = (eventId, panelId, payload) =>
  api.patch(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/jury-head`, payload);
export const setEventPanelHeadLive = (eventId, panelId, payload) =>
  api.patch(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/panel-head`, payload);
export const replaceEventPanelJudge = (eventId, panelId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/replace-judge`, payload);
export const removeEventPanelJudge = (eventId, panelId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/remove-judge`, payload);
export const addEventPanelGuestJudge = (eventId, panelId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/guest-judges`, payload);
export const updateEventPanelGuestJudge = (eventId, panelId, judgeId, payload) =>
  api.patch(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/guest-judges/${judgeId}`, payload);
export const removeEventPanelGuestJudge = (eventId, panelId, judgeId) =>
  api.delete(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/guest-judges/${judgeId}`);
export const getEventPanelScheduledApplicants = (eventId, panelId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/scheduled-applicants`, { params });
export const verifyEventPanelScores = (eventId, panelId, roundId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/rounds/${roundId}/verify-scores`, payload);
export const verifyEventPanelJuryScores = (eventId, panelId, roundId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/rounds/${roundId}/verify-jury-scores`, payload);
export const getEventPanelScoreReview = (eventId, panelId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/rounds/${roundId}/score-review`, { params });
export const getDummyBindCandidates = (eventId, panelId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/rounds/${roundId}/dummy-bind-candidates`, { params });
export const bindDummyApplicant = (eventId, panelId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/rounds/${roundId}/bind-dummy`, payload);
export const getEventPanelAwardCandidates = (eventId, panelId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/rounds/${roundId}/award-candidates`, { params });
export const setEventPanelAwards = (eventId, panelId, roundId, payload) =>
  api.put(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/rounds/${roundId}/awards`, payload);
export const getPanelFinalistNominations = (eventId, panelId, roundId) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/rounds/${roundId}/finalist-nominations`);
export const setPanelFinalistNominations = (eventId, panelId, roundId, payload) =>
  api.put(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/rounds/${roundId}/finalist-nominations`, payload);
export const verifyEventPanelJuryAwards = (eventId, panelId, roundId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/rounds/${roundId}/verify-awards`, payload);

// ============================================================================
// PANEL TEMPLATES (global, reusable across events)
// ============================================================================

export const getPanelTemplates = () => api.get(`${EVENTS_BASE_URL}/panel-templates`);
export const getPanelTemplate = (templateId) => api.get(`${EVENTS_BASE_URL}/panel-templates/${templateId}`);
export const createPanelTemplate = (payload) => api.post(`${EVENTS_BASE_URL}/panel-templates`, payload);
export const updatePanelTemplate = (templateId, payload) => api.put(`${EVENTS_BASE_URL}/panel-templates/${templateId}`, payload);
export const deletePanelTemplate = (templateId) => api.delete(`${EVENTS_BASE_URL}/panel-templates/${templateId}`);

// ============================================================================
// ROUNDS
// ============================================================================
export const getEventRounds = (eventId) => api.get(`${EVENTS_BASE_URL}/events/${eventId}/rounds`);
export const createEventRound = (eventId, payload) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/rounds`, payload);
export const updateEventRound = (eventId, roundId, payload) => api.put(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}`, payload);
export const activateEventRound = (eventId, roundId) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}/activate`);
export const reopenEventRound = (eventId, roundId) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}/reopen`);
export const deactivateEventRound = (eventId, roundId) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}/deactivate`);
export const closeScoringRound = (eventId, roundId) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}/close-scoring`);
export const advanceSubmissions = (eventId, roundId, payload) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}/advance`, payload);
export const unadvanceSubmissions = (eventId, roundId, payload) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}/unadvance`, payload);
export const deleteEventRound = (eventId, roundId) => api.delete(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}`);

// ============================================================================
// SCHEDULE — SLOTS & ASSIGNMENTS
// ============================================================================
export const getScheduleSlots = (eventId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/slots`, { params });
export const getScheduleSlotDays = (eventId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/slots/days`, { params });
export const createScheduleSlot = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/slots`, payload);
export const updateScheduleSlot = (eventId, roundId, slotId, payload) =>
  api.put(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/slots/${slotId}`, payload);
export const updateSlotPlaceholder = (eventId, roundId, slotId, payload) =>
  api.put(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/slots/${slotId}/placeholder`, payload);
export const deleteScheduleSlot = (eventId, roundId, slotId) =>
  api.delete(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/slots/${slotId}`);
export const bulkUpdateScheduleSlots = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/slots/bulk-update`, payload);
export const bulkDeleteScheduleSlots = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/slots/bulk-delete`, payload);
export const getSlotAttendees = (eventId, roundId, slotId, config = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/slots/${slotId}/attendees`, config);

export const getScheduleAssignments = (eventId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments`, { params });
export const getAbsentScheduleAssignments = (eventId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/absent`, { params });
export const exportAbsentScheduleAssignmentsCsv = (eventId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/absent/export/csv`, {
    params,
    responseType: 'blob',
  });
export const exportAbsentScheduleAssignmentsPdf = (eventId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/absent/export/pdf`, {
    params,
    responseType: 'blob',
  });
export const getAssignableScheduleSubmissions = (eventId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/assignable-submissions`, { params });
export const assignToSlot = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments`, payload);
export const bulkAutoAssignSlots = (eventId, roundId) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/bulk`);
export const rescheduleAssignment = (eventId, roundId, assignmentId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/${assignmentId}/reschedule`, payload);
export const markNoShow = (eventId, roundId, assignmentId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/${assignmentId}/no-show`, payload);
export const rejectOnDay = (eventId, roundId, assignmentId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/${assignmentId}/reject-on-day`, payload);
export const sendSlotNotificationEmail = (eventId, roundId, assignmentId) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/${assignmentId}/notify`);

// Direct Judge Assignments (DIRECT_ASSIGNMENT rounds)
export const getDirectAssignmentBoard = (eventId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/direct-assignments`, { params });
/** Lazy, paginated list of the applicants assigned to one judge. */
export const getJudgeDirectAssignments = (eventId, roundId, judgeId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/direct-assignments/judges/${judgeId}`, { params });
/** Direct-assignment rounds: paged, searchable brand / name values of the applications at judging level. */
export const getDirectAssignmentFilterOptions = (eventId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/direct-assignments/filter-options`, { params, skipErrorToast: true });
export const createDirectAssignments = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/direct-assignments`, payload);
export const removeDirectAssignments = (eventId, roundId, payload = {}, params = {}) =>
  api.delete(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/direct-assignments`, { data: payload, params });
// Billing-only events: direct invoicing of external companies
const directBase = (eventId) => `${EVENTS_BASE_URL}/direct-billing/${eventId}/invoices`;
export const listDirectInvoices = (eventId, params = {}) => api.get(directBase(eventId), { params });
export const getDirectInvoice = (eventId, invoiceId) => api.get(`${directBase(eventId)}/${invoiceId}`);
export const createDirectInvoice = (eventId, payload) => api.post(directBase(eventId), payload);
export const updateDirectInvoice = (eventId, invoiceId, payload) => api.put(`${directBase(eventId)}/${invoiceId}`, payload);
export const issueDirectProforma = (eventId, invoiceId) => api.post(`${directBase(eventId)}/${invoiceId}/proforma`);
export const issueDirectInvoice = (eventId, invoiceId) => api.post(`${directBase(eventId)}/${invoiceId}/invoice`);
export const requestDirectInvoice = (eventId, invoiceId, payload = {}) => api.post(`${directBase(eventId)}/${invoiceId}/request-invoice`, payload);
export const sendDirectPaymentLink = (eventId, invoiceId, payload) => api.post(`${directBase(eventId)}/${invoiceId}/payment-link/send`, payload);
export const recordDirectPayment = (eventId, invoiceId, payload) => api.post(`${directBase(eventId)}/${invoiceId}/payments`, payload);
export const cancelDirectInvoice = (eventId, invoiceId, payload) => api.post(`${directBase(eventId)}/${invoiceId}/cancel`, payload);
export const reverseDirectPayment = (eventId, invoiceId, payload) => api.post(`${directBase(eventId)}/${invoiceId}/payments/reverse`, payload);
export const createDirectPaymentLink = (eventId, invoiceId, payload = {}) => api.post(`${directBase(eventId)}/${invoiceId}/payment-link`, payload);
export const cancelDirectPaymentLink = (eventId, invoiceId) => api.post(`${directBase(eventId)}/${invoiceId}/payment-link/cancel`);
export const approveDirectPendingPayment = (eventId, invoiceId, pendingId, payload = {}) => api.post(`${directBase(eventId)}/${invoiceId}/pending-payments/${pendingId}/approve`, payload);
export const checkDirectPendingDuplicate = (eventId, invoiceId, pendingId) => api.get(`${directBase(eventId)}/${invoiceId}/pending-payments/${pendingId}/duplicate-check`);
export const reopenDirectRejectedPayment = (eventId, invoiceId, pendingId, payload = {}) => api.post(`${directBase(eventId)}/${invoiceId}/pending-payments/${pendingId}/reopen`, payload);
export const rejectDirectPendingPayment = (eventId, invoiceId, pendingId, payload) => api.post(`${directBase(eventId)}/${invoiceId}/pending-payments/${pendingId}/reject`, payload);
export const getDirectLineOptions = (eventId, params = {}) => api.get(`${EVENTS_BASE_URL}/direct-billing/${eventId}/line-options`, { params });
export const downloadDirectDocumentPdf = (eventId, invoiceId, type, params = {}) => api.get(`${directBase(eventId)}/${invoiceId}/pdf/${type}`, { params, responseType: 'blob' });
export const emailDirectDocument = (eventId, invoiceId, payload) => api.post(`${directBase(eventId)}/${invoiceId}/email`, payload);
export const exportDirectInvoicesCsv = (eventId, params = {}) => api.get(`${directBase(eventId)}/export/csv`, { params, responseType: 'blob' });
export const exportDirectInvoicesPdf = (eventId, params = {}) => api.get(`${directBase(eventId)}/export/pdf`, { params, responseType: 'blob' });
// Public payment link (no login)
const directPublic = (token) => `${EVENTS_BASE_URL}/direct-billing/public/${token}`;
export const getPublicDirectPayment = (token) => api.get(directPublic(token), { skipErrorToast: true });
export const downloadPublicDirectDocumentPdf = (token, type, params = {}) => api.get(`${directPublic(token)}/pdf/${type}`, { params, responseType: 'blob' });
export const submitPublicDirectOfflinePayment = (token, payload) => api.post(`${directPublic(token)}/offline`, payload);
export const initiatePublicDirectCardPayment = (token, payload) => api.post(`${directPublic(token)}/card`, payload);

export const autoDistributeDirectAssignments = (eventId, roundId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/direct-assignments/auto-distribute`, payload);

export const generateScheduleSlots = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/slots/generate`, payload);
export const notifyAllApplicants = (eventId, roundId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/notify-all`, payload);
export const notifyJudges = (eventId, roundId) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/notify-judges`);
export const notifyCoordinators = (eventId, roundId) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/notify-coordinators`);
export const notifyJudgingDay = (eventId, roundId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/notify-judging-day`, payload);
export const assignAllMatchingToSlot = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/bulk-to-slot`, payload);
export const revokeEventPanelScoreVerification = (eventId, panelId, roundId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/rounds/${roundId}/revoke-score-verification`, payload);
export const requestEventPanelRescore = (eventId, panelId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/panels/${panelId}/rounds/${roundId}/request-rescore`, payload);
export const bulkAssignSubmissionsToSlot = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/bulk-assign`, payload);
export const commitScheduleDraft = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/commit-draft`, payload);
export const fillSlotWithPlaceholder = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/fill-placeholder`, payload);
export const unassignFromSlot = (eventId, roundId, assignmentId) =>
  api.delete(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/${assignmentId}`);
export const swapScheduleAssignments = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/swap`, payload);
export const holdGradingForAssignment = (eventId, roundId, assignmentId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/${assignmentId}/hold-grading`, payload);
export const releaseGradingForAssignment = (eventId, roundId, assignmentId) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/assignments/${assignmentId}/release-grading`);
export const getScheduleRescheduleRequests = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/reschedule-requests`, { params });
export const createScheduleRescheduleRequest = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/reschedule-requests`, payload);
export const approveScheduleRescheduleRequest = (eventId, requestId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/reschedule-requests/${requestId}/approve`, payload);
export const rejectScheduleRescheduleRequest = (eventId, requestId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/reschedule-requests/${requestId}/reject`, payload);
export const getCoordinatorEventResults = (eventId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/coordinator/event-results`, { params: { eventId } });

// ============================================================================
// JUDGE SCORES
// ============================================================================
export const getMyJudgeAssignments = (params = {}, config = {}) =>
  api.get(`${EVENTS_BASE_URL}/judge-scores/my-assignments`, { params, ...config });
/** Judge Portal: scroll-loaded Brand / Name values across the judge's direct-assignment applicants. */
export const getMyJudgeBrandOptions = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/judge-scores/my-assignments/brand-options`, { params, skipErrorToast: true });
export const getMyJudgeAssignmentAttendance = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/judge-scores/my-assignments/attendance`, { params });
export const getMyJudgeScoringHistory = (params = {}) => api.get(`${EVENTS_BASE_URL}/judge-scores/my-history`, { params });

/**
 * Load all my-history rows for a single panel/event scope by paging (max 100/request).
 * Server sorts each page by sortBy across the full matching set, so concatenating
 * pages preserves global rank order without raising the API limit.
 */
export async function fetchAllMyJudgeScoringHistory(params = {}, { pageSize = 100, maxPages = 50 } = {}) {
  const limit = Math.min(100, Math.max(1, pageSize));
  const base = { ...params, limit };
  delete base.panels; // multi-panel shortcut cannot page correctly per panel
  delete base.page;

  let page = 1;
  let history = [];
  let summary = { total: 0, submitted: 0, draft: 0 };
  let pages = 1;
  let total = null;

  while (page <= maxPages) {
    const res = await getMyJudgeScoringHistory({ ...base, page });
    const data = res.data?.data || {};
    const chunk = Array.isArray(data.history) ? data.history : [];
    history = history.concat(chunk);
    if (data.summary) summary = data.summary;
    pages = Math.max(1, Number(data.pagination?.pages) || 1);
    total = data.pagination?.total != null ? Number(data.pagination.total) : total;

    if (!chunk.length) break;
    if (total != null && history.length >= total) break;
    if (page >= pages) break;
    page += 1;
  }

  return {
    history,
    summary: {
      total: total != null ? total : (summary.total || history.length),
      submitted: summary.submitted || 0,
      draft: summary.draft || 0,
    },
    pagination: {
      page: 1,
      limit: history.length,
      total: total != null ? total : history.length,
      pages: 1,
    },
  };
}

export const getJudgeReallocatableSlots = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/judge-scores/reallocatable-slots`, { params });
export const reallocateAsJudgePanelHead = (payload) =>
  api.post(`${EVENTS_BASE_URL}/judge-scores/reallocate`, payload);
export const getJudgeSubmissionDetail = (submissionId) => api.get(`${EVENTS_BASE_URL}/judge-scores/submissions/${submissionId}`);
export const downloadJudgeSubmissionPdf = (submissionId) =>
  api.get(`${EVENTS_BASE_URL}/judge-scores/submissions/${submissionId}/pdf`, { responseType: 'blob' });
export const getJudgeVerifiedSignedApplicationDocument = (submissionId) =>
  api.get(`${EVENTS_BASE_URL}/judge-scores/submissions/${submissionId}/signed-application-document`);
export const getJudgeCriteriaByCategory = (categoryId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/judge-scores/criteria/category/${categoryId}`, { params });
export const getSubmissionScores = (submissionId, roundId) =>
  api.get(`${EVENTS_BASE_URL}/judge-scores/submission/${submissionId}/round/${roundId}`);
export const saveJudgeScore = (payload) => api.post(`${EVENTS_BASE_URL}/judge-scores`, payload);
export const renameDummyApplicant = (submissionId, payload) =>
  api.patch(`${EVENTS_BASE_URL}/judge-scores/dummy-applicants/${submissionId}`, payload);
export const deleteDummyApplicant = (submissionId) =>
  api.delete(`${EVENTS_BASE_URL}/judge-scores/dummy-applicants/${submissionId}`);
export const addDummyApplicant = (payload) =>
  api.post(`${EVENTS_BASE_URL}/judge-scores/dummy-applicants`, payload);
export const getWalkInCategoryOptions = (panelId) =>
  api.get(`${EVENTS_BASE_URL}/judge-scores/panels/${panelId}/walk-in-options`);
export const fillSlotWithPlaceholderAsJudge = (payload) =>
  api.post(`${EVENTS_BASE_URL}/judge-scores/assignments/fill-placeholder`, payload);
export const submitJudgeScore = (scoreId) => api.post(`${EVENTS_BASE_URL}/judge-scores/${scoreId}/submit`);
export const bulkSubmitJudgeScores = (scoreIds) =>
  api.post(`${EVENTS_BASE_URL}/judge-scores/bulk-submit`, { scoreIds });
export const overrideJudgeScore = (scoreId, payload) => api.put(`${EVENTS_BASE_URL}/judge-scores/${scoreId}/override`, payload);
export const getScoringsSummary = (eventId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/judge-scores/${eventId}/rounds/${roundId}/scoring-summary`, { params });
/** Scoring review: paged, searchable options for the category / company / judge filters. */
export const getScoringSummaryFilterOptions = (eventId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/judge-scores/${eventId}/rounds/${roundId}/filter-options`, { params, skipErrorToast: true });
export const sendJudgingReminders = (eventId, roundId) =>
  api.post(`${EVENTS_BASE_URL}/judge-scores/${eventId}/rounds/${roundId}/send-judging-reminders`);

// ============================================================================
// AUDITOR PORTAL (read-only)
// ============================================================================
export const getAuditorPortalEvents = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/auditor-portal/events`, { params });
export const getAuditorPortalEventOverview = (eventId) =>
  api.get(`${EVENTS_BASE_URL}/auditor-portal/events/${eventId}`);
export const getAuditorPortalSubmissions = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/auditor-portal/events/${eventId}/submissions`, { params });
export const getAuditorPortalSubmissionDetail = (eventId, submissionId) =>
  api.get(`${EVENTS_BASE_URL}/auditor-portal/events/${eventId}/submissions/${submissionId}`);
export const downloadAuditorPortalSubmissionPdf = (eventId, submissionId) =>
  api.get(`${EVENTS_BASE_URL}/auditor-portal/events/${eventId}/submissions/${submissionId}/pdf`, { responseType: 'blob' });
export const getAuditorPortalRoundScoring = (eventId, roundId) =>
  api.get(`${EVENTS_BASE_URL}/auditor-portal/events/${eventId}/rounds/${roundId}/scoring`);
export const getAuditorPortalRoundResults = (eventId, roundId) =>
  api.get(`${EVENTS_BASE_URL}/auditor-portal/events/${eventId}/rounds/${roundId}/results`);
export const getAuditorPortalSchedule = (eventId) =>
  api.get(`${EVENTS_BASE_URL}/auditor-portal/events/${eventId}/schedule`);
export const getAuditorPortalFeedback = (eventId) =>
  api.get(`${EVENTS_BASE_URL}/auditor-portal/events/${eventId}/feedback`);

// ============================================================================
// RESULTS & DELIBERATION
// ============================================================================
/** Results (direct-assignment rounds): scroll-loaded Brand / Name values of the round's applicants. */
export const getEventResultFilterOptions = (eventId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}/results/filter-options`, { params, skipErrorToast: true });
export const calculateEventResults = (eventId, roundId) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}/results/calculate`);
export const getEventResults = (eventId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}/results`, { params });
export const shortlistResults = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}/results/shortlist`, payload);
export const getShortlist = (eventId, roundId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}/results/shortlist`, { params });
export const updateDeliberation = (eventId, roundId, resultId, payload) =>
  api.put(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}/results/${resultId}/deliberation`, payload);
export const publishEventResults = (eventId, roundId) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/rounds/${roundId}/results/publish`);
export const getPublicResults = (eventId) => api.get(`${EVENTS_BASE_URL}/events/${eventId}/results/public`);

// ============================================================================
// CHECK-IN
// ============================================================================
export const checkInParticipant = (eventId, payload) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/check-in`, payload);
export const bulkCheckInParticipants = (eventId, payload) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/check-in/bulk`, payload);
export const checkInDayParticipants = (eventId, payload) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/check-in/day`, payload);
export const getCheckInApplicants = (eventId, params = {}) => api.get(`${EVENTS_BASE_URL}/events/${eventId}/check-in/applicants`, { params });
export const getCheckInCompanies = (eventId, params = {}) => api.get(`${EVENTS_BASE_URL}/events/${eventId}/check-in/companies`, { params });
export const getCheckInJudges = (eventId, params = {}) => api.get(`${EVENTS_BASE_URL}/events/${eventId}/check-in/judges`, { params });
export const getCheckIns = (eventId, params = {}) => api.get(`${EVENTS_BASE_URL}/events/${eventId}/check-in`, { params });
export const exportCheckInsCsv = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/check-in/export/csv`, { params, responseType: 'blob' });
export const exportCheckInsPdf = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/check-in/export/pdf`, { params, responseType: 'blob' });
export const getCheckInStats = (eventId, params = {}) => api.get(`${EVENTS_BASE_URL}/events/${eventId}/check-in/stats`, { params });
export const updateCheckIn = (eventId, checkInId, payload) => api.patch(`${EVENTS_BASE_URL}/events/${eventId}/check-in/${checkInId}`, payload);
export const undoCheckIn = (eventId, checkInId) => api.delete(`${EVENTS_BASE_URL}/events/${eventId}/check-in/${checkInId}`);
export const bulkUndoCheckIns = (eventId, payload) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/check-in/bulk-undo`, payload);

// ============================================================================
// CERTIFICATES
// ============================================================================
export const generateCertificates = (eventId) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/certificates/generate`);
export const getEventCertificates = (eventId, params = {}) => api.get(`${EVENTS_BASE_URL}/events/${eventId}/certificates`, { params });
export const verifyCertificate = (token) => api.get(`${EVENTS_BASE_URL}/certificates/verify/${token}`);

// ============================================================================
// FEEDBACK
// ============================================================================
export const getPublicEventFeedbackContext = (eventId) =>
  api.get(`${EVENTS_BASE_URL}/events/${eventId}/feedback/public`);
export const submitEventFeedback = (eventId, payload) => api.post(`${EVENTS_BASE_URL}/events/${eventId}/feedback`, payload);
export const getEventFeedback = (eventId, params = {}) => api.get(`${EVENTS_BASE_URL}/events/${eventId}/feedback`, { params });

// ============================================================================
// EXPORT & BULK OPERATIONS (ApplicationForm extensions)
// ============================================================================
export const exportSubmissionsCSV = (formId) =>
  api.get(`${EVENTS_BASE_URL}/application-forms/${formId}/submissions/export`, { responseType: 'blob' });
export const bulkApprovePaymentReferences = (formId, payload) =>
  api.post(`${EVENTS_BASE_URL}/application-forms/${formId}/payment-references/bulk-approve`, payload, eventHeavyRequest);

// ============================================================================
// EVENT SPONSORS & SPONSORSHIPS
// ============================================================================
export const getEventSponsors = (params = {}) => api.get(`${EVENTS_BASE_URL}/sponsors`, { params });
export const getEventSponsorOptions = (params = {}) => api.get(`${EVENTS_BASE_URL}/sponsors/options`, { params });
export const getEventSponsorById = (id) => api.get(`${EVENTS_BASE_URL}/sponsors/${id}`);
export const createEventSponsor = (payload) => api.post(`${EVENTS_BASE_URL}/sponsors`, payload);
export const updateEventSponsor = (id, payload) => api.put(`${EVENTS_BASE_URL}/sponsors/${id}`, payload);
export const deleteEventSponsor = (id) => api.delete(`${EVENTS_BASE_URL}/sponsors/${id}`);

export const getEventSponsorships = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/sponsorships/by-event/${eventId}`, { params });
export const getEventSponsorshipById = (eventId, sponsorshipId) =>
  api.get(`${EVENTS_BASE_URL}/sponsorships/by-event/${eventId}/${sponsorshipId}`);
export const createEventSponsorship = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/sponsorships/by-event/${eventId}`, payload);
export const updateEventSponsorship = (eventId, sponsorshipId, payload) =>
  api.put(`${EVENTS_BASE_URL}/sponsorships/by-event/${eventId}/${sponsorshipId}`, payload);
export const generateEventSponsorshipPerforma = (eventId, sponsorshipId) =>
  api.post(`${EVENTS_BASE_URL}/sponsorships/by-event/${eventId}/${sponsorshipId}/performa-invoice`, {});
export const issueEventSponsorshipTaxInvoice = (eventId, sponsorshipId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/sponsorships/by-event/${eventId}/${sponsorshipId}/tax-invoice`, payload);
export const createEventSponsorshipPaymentLink = (eventId, sponsorshipId) =>
  api.post(`${EVENTS_BASE_URL}/sponsorships/by-event/${eventId}/${sponsorshipId}/payment-link`, {});
export const cancelEventSponsorship = (eventId, sponsorshipId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/sponsorships/by-event/${eventId}/${sponsorshipId}/cancel`, payload);
export const downloadEventSponsorshipPerformaPdf = (eventId, sponsorshipId) =>
  api.get(`${EVENTS_BASE_URL}/sponsorships/by-event/${eventId}/${sponsorshipId}/performa-invoice/pdf`, { responseType: 'blob' });
export const downloadEventSponsorshipTaxInvoicePdf = (eventId, sponsorshipId, copy = 'duplicate') =>
  api.get(`${EVENTS_BASE_URL}/sponsorships/by-event/${eventId}/${sponsorshipId}/tax-invoice/pdf`, {
    responseType: 'blob',
    params: { copy, _: Date.now() },
  });
export const downloadEventSponsorshipReceiptPdf = (eventId, sponsorshipId, paymentKey) =>
  api.get(`${EVENTS_BASE_URL}/sponsorships/by-event/${eventId}/${sponsorshipId}/receipts/${paymentKey}/pdf`, { responseType: 'blob' });
export const emailEventSponsorshipPaymentReceipt = (eventId, sponsorshipId, paymentKey, payload = {}) =>
  api.post(
    `${EVENTS_BASE_URL}/sponsorships/by-event/${eventId}/${sponsorshipId}/receipts/${encodeURIComponent(paymentKey)}/email`,
    payload
  );
export const approveEventSponsorshipPayment = (sponsorshipId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/sponsorships/payments/${sponsorshipId}/approve`, payload);
export const rejectEventSponsorshipPayment = (sponsorshipId, payload) =>
  api.post(`${EVENTS_BASE_URL}/sponsorships/payments/${sponsorshipId}/reject`, payload);

export const getPublicSponsorshipPayment = (token) =>
  api.get(`${EVENTS_BASE_URL}/sponsorships/public/payments/${token}`);
export const downloadPublicSponsorshipTaxInvoicePdf = (token) =>
  api.get(`${EVENTS_BASE_URL}/sponsorships/public/payments/${token}/tax-invoice/pdf`, { responseType: 'blob' });
export const downloadPublicSponsorshipReceiptPdf = (token, paymentKey = 'latest') =>
  api.get(`${EVENTS_BASE_URL}/sponsorships/public/payments/${token}/receipts/${paymentKey}/pdf`, { responseType: 'blob' });
export const initiatePublicSponsorshipCardPayment = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/sponsorships/public/payments/${token}/initiate-card`, payload);
export const submitPublicSponsorshipOfflinePayment = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/sponsorships/public/payments/${token}/submit-offline`, payload);

// Sponsor contracts
export const getSponsorContracts = (params = {}) => api.get(`${EVENTS_BASE_URL}/sponsor-contracts`, { params });
export const getSponsorContractPayments = (params = {}) => api.get(`${EVENTS_BASE_URL}/sponsor-contracts/payments`, { params });
export const getSponsorContractById = (contractId) => api.get(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}`);
export const getSponsorContractPaymentAccounts = (contractId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/payment-accounts`, { params });
export const getSponsorContractOverview = (contractId) => api.get(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/overview`);
export const getSponsorContractFundingReport = (params = {}) => api.get(`${EVENTS_BASE_URL}/sponsor-contracts/funding-report`, { params });
export const createSponsorContract = (payload) => api.post(`${EVENTS_BASE_URL}/sponsor-contracts`, payload);
export const updateSponsorContract = (contractId, payload) => api.put(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}`, payload);
export const issueSponsorContract = (contractId) => api.post(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/issue-contract`, {});
export const generateSponsorContractPerforma = (contractId) => api.post(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/performa-invoice`, {});
export const issueSponsorContractTaxInvoice = (contractId, payload = {}) => api.post(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/tax-invoice`, payload);
export const createSponsorContractPaymentLink = (contractId) => api.post(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/payment-link`, {});
export const updateSponsorContractPaymentLinkValidity = (contractId, payload) =>
  api.post(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/payment-link/validity`, payload);
export const recordSponsorContractManualPayment = (contractId, payload) => api.post(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/manual-payment`, payload);
export const approveSponsorContractPayment = (contractId, payload = {}) => api.post(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/payments/approve`, payload);
export const rejectSponsorContractPayment = (contractId, payload) => api.post(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/payments/reject`, payload);
export const cancelSponsorContract = (contractId, payload = {}) => api.post(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/cancel`, payload);
export const downloadSponsorContractPdf = (contractId) => api.get(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/contract/pdf`, { responseType: 'blob' });
export const downloadSponsorContractPerformaPdf = (contractId) => api.get(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/performa-invoice/pdf`, { responseType: 'blob' });
export const downloadSponsorContractTaxInvoicePdf = (contractId, copy = 'duplicate') =>
  api.get(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/tax-invoice/pdf`, {
    responseType: 'blob',
    params: { copy, _: Date.now() },
  });
export const downloadSponsorContractReceiptPdf = (contractId, paymentKey) => api.get(`${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/receipts/${encodeURIComponent(paymentKey)}/pdf`, { responseType: 'blob' });
export const emailSponsorContractPaymentReceipt = (contractId, paymentKey, payload = {}) =>
  api.post(
    `${EVENTS_BASE_URL}/sponsor-contracts/${contractId}/receipts/${encodeURIComponent(paymentKey)}/email`,
    payload
  );

export const getPublicSponsorContractPayment = (token) => api.get(`${EVENTS_BASE_URL}/sponsor-contracts/public/payments/${token}`);
export const downloadPublicSponsorContractTaxInvoicePdf = (token) => api.get(`${EVENTS_BASE_URL}/sponsor-contracts/public/payments/${token}/tax-invoice/pdf`, { responseType: 'blob' });
export const downloadPublicSponsorContractReceiptPdf = (token, paymentKey = 'latest') => api.get(`${EVENTS_BASE_URL}/sponsor-contracts/public/payments/${token}/receipts/${encodeURIComponent(paymentKey)}/pdf`, { responseType: 'blob' });
export const initiatePublicSponsorContractCardPayment = (token, payload) => api.post(`${EVENTS_BASE_URL}/sponsor-contracts/public/payments/${token}/initiate-card`, payload);
export const submitPublicSponsorContractOfflinePayment = (token, payload) => api.post(`${EVENTS_BASE_URL}/sponsor-contracts/public/payments/${token}/submit-offline`, payload);

// ============================================================================
// FINANCE GL REVERSALS (audit-only — business status unchanged)
// ============================================================================

export const reverseEventProformaFinancePosting = (requestId, payload) =>
  api.post(`${EVENTS_BASE_URL}/finance-reversals/proforma/${requestId}`, payload);

export const reverseEventPaymentReferenceFinancePosting = (paymentReferenceId, payload) =>
  api.post(`${EVENTS_BASE_URL}/finance-reversals/payment-reference/${paymentReferenceId}`, payload);

export const reverseEventSponsorshipFinancePosting = (sponsorshipId, payload) =>
  api.post(`${EVENTS_BASE_URL}/finance-reversals/sponsorship/${sponsorshipId}`, payload);

export const reverseEventSponsorContractFinancePosting = (contractId, payload) =>
  api.post(`${EVENTS_BASE_URL}/finance-reversals/sponsor-contract/${contractId}`, payload);

// ============================================================================
// AUDIT LOGS (Administration → Report → Audit Logs → Event)
// ============================================================================

export const getEventAuditLogs = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/reports/audit-logs`, { params });

export const exportEventAuditLogs = (payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/reports/audit-logs/export`, payload, { responseType: 'blob' });

export const reportsApi = {
  getAuditLogs: getEventAuditLogs,
  exportAuditLogs: exportEventAuditLogs,
};

// ============================================================================
// EVENT TICKETING (parallel domain)
// ============================================================================

export const getEventTicketConfig = (eventId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/config`);
export const updateEventTicketConfig = (eventId, payload) =>
  api.put(`${EVENTS_BASE_URL}/tickets/events/${eventId}/config`, payload);
export const openEventTicketSales = (eventId) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/sales/open`, {});
export const closeEventTicketSales = (eventId) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/sales/close`, {});
export const notifyEventTicketSales = (eventId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/sales/notify`, payload);

export const getEventTicketTypes = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/types`, { params });
export const createEventTicketType = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/types`, payload);
export const updateEventTicketType = (eventId, typeId, payload) =>
  api.put(`${EVENTS_BASE_URL}/tickets/events/${eventId}/types/${typeId}`, payload);
export const deleteEventTicketType = (eventId, typeId) =>
  api.delete(`${EVENTS_BASE_URL}/tickets/events/${eventId}/types/${typeId}`);
export const exportEventTicketTypesCsv = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/types/export/csv`, { params, responseType: 'blob' });
export const exportEventTicketTypesPdf = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/types/export/pdf`, { params, responseType: 'blob' });

export const getEventTicketBillingSummary = (eventId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/summary`);
export const getEventTicketsSummary = (eventId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets/summary`);
export const getEventTicketRequestsSummary = (eventId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/requests/summary`);
export const getEventTicketRequests = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/requests`, { params });
export const createEventTicketRequest = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/requests`, payload);
export const getPortalEventTickets = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/my-tickets`, { params: { ...params, eventId } });
export const transferPortalEventTicket = (eventId, ticketId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/portal/tickets/${ticketId}/transfer`, payload);
export const bulkAssignPortalEventTickets = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/portal/tickets/bulk-assign`, payload);
export const approveEventTicketRequest = (eventId, requestId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/requests/${requestId}/approve`, payload);
export const rejectEventTicketRequest = (eventId, requestId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/requests/${requestId}/reject`, payload);
export const cancelEventTicketRequest = (eventId, requestId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/requests/${requestId}/cancel`, payload);
export const cancelEventTicketBilling = (eventId, billingId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/cancel`, payload);

export const getEventTicketBilling = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing`, { params });
export const getEventTicketBillingById = (eventId, billingId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}`);
export const submitEventTicketBillingOffline = (eventId, billingId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/submit-offline`, payload);
export const updateEventTicketProforma = (eventId, billingId, payload) =>
  api.put(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/proforma`, payload);
export const issueEventTicketTaxInvoice = (eventId, billingId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/tax-invoice`, payload);
export const issueEventTicketInvoice = (eventId, billingId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/invoice`, payload);
export const requestEventTicketTaxInvoice = (eventId, billingId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/request-tax-invoice`, payload);
export const requestEventTicketRegularInvoice = (eventId, billingId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/request-regular-invoice`, payload);
export const issueEventTicketsEarlyOverride = (eventId, billingId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/issue-tickets-override`, payload);
export const calculateEventTicketSummary = (eventId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/calculate-summary`, payload);
export const updateEventTicketHolder = (eventId, ticketId, payload = {}) =>
  api.patch(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets/${ticketId}/holder`, payload);
export const shareEventTicketBillingInvoice = (eventId, billingId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/share-invoice`, payload);
export const extendEventTicketPaymentLink = (eventId, billingId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/payment-link/extend`, payload);
export const createEventTicketPaymentLink = (eventId, billingId) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/payment-link`, {});
export const recordEventTicketManualPayment = (eventId, billingId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/manual-payment`, payload);
/** Event-wide seat numbers: instant availability check, and all-or-nothing save of [{ ticketId, seatNumber|null }]. */
export const checkEventTicketSeats = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets/seats/check`, payload);
export const saveEventTicketSeats = (eventId, seats) =>
  api.put(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets/seats`, { seats });
export const approveEventTicketPayment = (eventId, billingId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/payments/approve`, payload);
export const rejectEventTicketPayment = (eventId, billingId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/payments/reject`, payload);
export const reverseEventTicketPayment = (eventId, billingId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/payments/reverse`, payload);
export const checkEventTicketDuplicatePayment = (eventId, billingId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/duplicate-check`);
export const resendEventTicketBillingConfirmation = (eventId, billingId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/resend-tickets-email`, payload);
export const resendEventTicketConfirmation = (eventId, ticketId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets/${ticketId}/resend-email`, payload);
export const staffEventTicketCheckout = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/staff-checkout`, payload);
export const staffEventTicketBulkCheckout = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/staff-checkout/bulk`, payload, { timeout: 120000 });
export const getEventTicketStaffCompanyMatch = (eventId, email) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/staff-checkout/company-match`, { params: { email } });

export const getEventTickets = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets`, { params });
export const createEventTicketTransfer = (eventId, ticketId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets/${ticketId}/transfer`, payload);
export const getEventTicketTransfers = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/transfers`, { params });
export const cancelEventTicketTransfer = (eventId, transferId) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/transfers/${transferId}/cancel`, {});
export const previewEventTicketWinnerPasses = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/winner-passes/preview`, { params });
export const issueEventTicketWinnerPasses = (eventId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/winner-passes/issue`, payload);
export const getEventTicketReportSummary = (eventId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/reports/summary`);
export const checkInEventTicket = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in`, payload);
export const getTicketScanStationEvents = () =>
  api.get(`${EVENTS_BASE_URL}/tickets/scan-station/events`);
export const scanEventTicket = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/scan`, payload);
export const getTicketScanLog = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/scan-log`, { params });
export const getTicketScanStationStats = (eventId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/scan-stats`);
export const searchTicketScanStation = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/scan-search`, { params });
export const undoTicketScanStationCheckIn = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/scan-undo`, payload);
export const undoTicketScanStationBulk = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/scan-bulk-undo`, payload);
export const updateEventTicketRequest = (eventId, requestId, payload = {}) =>
  api.put(`${EVENTS_BASE_URL}/tickets/events/${eventId}/requests/${requestId}`, payload);
export const getEventTicketParties = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/parties`, { params });
export const confirmEventTicketInvoices = (eventId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/bulk-confirm`, payload);
export const revokeEventTicketInvoiceConfirmation = (eventId, billingId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/revoke-confirmation`, payload);
export const reopenEventTicketBilling = (eventId, billingId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/reopen`, payload);
export const reopenEventTicketRequest = (eventId, requestId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/requests/${requestId}/reopen`, payload);
export const reinstateEventTicket = (eventId, ticketId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets/${ticketId}/reinstate`, payload);
export const revertEventTicketTransfer = (eventId, ticketId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets/${ticketId}/revert-transfer`, payload);
export const getTicketScanStationCompanies = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/scan-companies`, { params });
export const getTicketScanStationCompanyManifest = (eventId, companyId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/scan-companies/${encodeURIComponent(companyId)}/manifest`);
export const admitTicketScanStationCompany = (eventId, companyId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/scan-companies/${encodeURIComponent(companyId)}/admit`, payload);
export const undoEventTicketCheckIn = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/undo`, payload);
export const cancelEventTicket = (eventId, ticketId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets/${ticketId}/cancel`, payload);
export const bulkCheckInEventTickets = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/bulk`, payload);
export const bulkUndoEventTicketCheckIns = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/bulk-undo`, payload);
export const getEventTicketDoorCheckInToken = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/door-token`, { params });
export const rotateEventTicketDoorCheckInToken = (eventId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/door-token/rotate`, payload);
export const getPublicEventTicketDoorCheckInPage = (token) =>
  api.get(`${EVENTS_BASE_URL}/tickets/public/door-check-in/${token}`);
export const submitPublicEventTicketDoorCheckIn = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/public/door-check-in/${token}`, payload);
export const getEventTicketCheckInCompanies = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/companies`, { params });
export const getEventTicketCompanyManifest = (eventId, companyId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/companies/${encodeURIComponent(companyId)}/manifest`);
export const admitEventTicketCompany = (eventId, companyId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/companies/${encodeURIComponent(companyId)}/admit`, payload);
export const createEventTicketCompanyManifestToken = (eventId, companyId) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/companies/${encodeURIComponent(companyId)}/manifest-token`, {});
export const exportEventTicketCompanyManifestCsv = (eventId, companyId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-in/companies/${encodeURIComponent(companyId)}/manifest/export/csv`, {
    responseType: 'blob',
  });
export const getEventTicketCheckIns = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-ins`, { params });
export const getPublicEventTicketCompanyManifest = (token) =>
  api.get(`${EVENTS_BASE_URL}/tickets/public/check-in/manifest/${token}`);

export const exportEventTicketRequestsCsv = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/requests/export/csv`, { params, responseType: 'blob' });
export const exportEventTicketRequestsPdf = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/requests/export/pdf`, { params, responseType: 'blob' });
export const exportEventTicketBillingCsv = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/export/csv`, { params, responseType: 'blob' });
export const exportEventTicketBillingPdf = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/export/pdf`, { params, responseType: 'blob' });
export const downloadEventTicketProformaPdf = (eventId, billingId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/proforma/pdf`, { responseType: 'blob' });
export const downloadEventTicketTaxInvoicePdf = (eventId, billingId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/tax-invoice/pdf`, { params, responseType: 'blob' });
export const downloadEventTicketInvoicePdf = (eventId, billingId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/invoice/pdf`, { responseType: 'blob' });
export const downloadEventTicketReceiptPdf = (eventId, billingId, paymentKey = 'latest') =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/receipts/${paymentKey}/pdf`, { responseType: 'blob' });
export const getEventTicketWorkshops = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/workshops`, { params });
export const createEventTicketWorkshop = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/workshops`, payload);
export const updateEventTicketWorkshop = (eventId, workshopId, payload) =>
  api.put(`${EVENTS_BASE_URL}/tickets/events/${eventId}/workshops/${workshopId}`, payload);
export const deleteEventTicketWorkshop = (eventId, workshopId) =>
  api.delete(`${EVENTS_BASE_URL}/tickets/events/${eventId}/workshops/${workshopId}`);
export const initiatePublicEventTicketCardPayment = (token, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/public/payments/${token}/initiate-card`, payload);
export const downloadPublicEventTicketProformaPdf = (token) =>
  api.get(`${EVENTS_BASE_URL}/tickets/public/payments/${token}/proforma/pdf`, { responseType: 'blob' });
export const downloadPublicEventTicketTaxInvoicePdf = (token) =>
  api.get(`${EVENTS_BASE_URL}/tickets/public/payments/${token}/tax-invoice/pdf`, { responseType: 'blob' });
export const downloadPublicEventTicketInvoicePdf = (token) =>
  api.get(`${EVENTS_BASE_URL}/tickets/public/payments/${token}/invoice/pdf`, { responseType: 'blob' });
export const downloadEventTicketPreprintedReceiptPdf = (eventId, billingId, paymentKey = 'latest') =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/billing/${billingId}/receipts/${paymentKey}/preprinted/pdf`, { responseType: 'blob' });
export const downloadPublicEventTicketReceiptPdf = (token, paymentKey = 'latest') =>
  api.get(`${EVENTS_BASE_URL}/tickets/public/payments/${token}/receipts/${paymentKey}/pdf`, { responseType: 'blob' });
export const exportEventTicketsCsv = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets/export/csv`, { params, responseType: 'blob' });
export const exportEventTicketsPdf = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets/export/pdf`, { params, responseType: 'blob' });
export const exportEventTicketCheckInsCsv = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-ins/export/csv`, { params, responseType: 'blob' });
export const exportEventTicketCheckInsPdf = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/check-ins/export/pdf`, { params, responseType: 'blob' });

export const getEventTicketFloorPlan = (eventId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/floor-plan`);
export const getEventVenueTables = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables`, { params });
export const createEventVenueTable = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables`, payload);
export const bulkCreateEventVenueTables = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/bulk-create`, payload);
export const bulkUpdateEventVenueTableTypes = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/bulk-update-type`, payload);
export const bulkUpdateEventVenueTablePositions = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/bulk-positions`, payload);
export const bulkUnassignEventVenueTables = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/bulk-unassign`, payload);
export const transferEventTableOccupants = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/transfer`, payload);
export const updateEventVenueTable = (eventId, tableId, payload) =>
  api.put(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/${tableId}`, payload);
export const deleteEventVenueTable = (eventId, tableId) =>
  api.delete(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/${tableId}`);
export const issueComplimentaryEventTickets = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets/issue-complimentary`, payload);
export const bulkIssueComplimentaryEventTickets = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets/bulk-issue-complimentary`, payload);
export const broadcastEventTicketEmails = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tickets/broadcast-email`, payload);
export const getEventTableAssignments = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/assignments`, { params });
export const getEventTableUnassignedTickets = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/unassigned-tickets`, { params });
export const manualAssignEventTableSeat = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/assignments/manual`, payload);
export const bulkAssignEventTableSeats = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/assignments/bulk-manual`, payload);
export const autoAssignEventTableSeats = (eventId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/assignments/auto-assign`, payload);
export const reassignEventTableSeat = (eventId, assignmentId, payload) =>
  api.put(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/assignments/${assignmentId}`, payload);
export const unassignEventTableSeat = (eventId, assignmentId) =>
  api.delete(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/assignments/${assignmentId}`);
export const exportEventTableAssignmentsCsv = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/assignments/export/csv`, { params, responseType: 'blob' });
export const exportEventTableAssignmentsPdf = (eventId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/tickets/events/${eventId}/tables/assignments/export/pdf`, { params, responseType: 'blob' });

export const getPublicEventTicketStorefront = (eventId) =>
  api.get(`${EVENTS_BASE_URL}/tickets/public/${eventId}/storefront`);
export const publicEventTicketCalculateSummary = (eventId, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/public/${eventId}/calculate-summary`, payload);
export const publicEventTicketCheckout =(eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/public/${eventId}/checkout`, payload);
export const publicEventTicketCompanyRequest = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/public/${eventId}/requests`, payload);
export const getPublicEventTicketPayment = (token) =>
  api.get(`${EVENTS_BASE_URL}/tickets/public/payments/${token}`);
export const submitPublicEventTicketOfflinePayment = (token, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/public/payments/${token}/submit-offline`, payload);
export const getPublicEventTicketTransfer = (token) =>
  api.get(`${EVENTS_BASE_URL}/tickets/public/transfers/${token}`);
export const acceptPublicEventTicketTransfer = (token, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/tickets/public/transfers/${token}/accept`, payload);
export const requestPublicEventTicketTransfer = (eventId, payload) =>
  api.post(`${EVENTS_BASE_URL}/tickets/public/${eventId}/transfers/request`, payload);

// Excel Day Schedule Import
export const previewExcelScheduleImport = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/schedule/import-excel/preview`, payload);
export const executeExcelScheduleImport = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/events/${eventId}/${roundId}/schedule/import-excel/execute`, payload);

// ============================================================================
// EVENT CREDIT NOTES (SPONSORS & EXTERNAL COMPANIES)
// ============================================================================

export const getEventCreditNotes = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/credit-notes`, { params });

export const getEventCreditNoteById = (id) =>
  api.get(`${EVENTS_BASE_URL}/credit-notes/${id}`);

export const createEventCreditNote = (payload) =>
  api.post(`${EVENTS_BASE_URL}/credit-notes`, payload);

export const createBulkEventCreditNotes = (payload) =>
  api.post(`${EVENTS_BASE_URL}/credit-notes/bulk`, payload);

export const updateEventCreditNote = (id, payload) =>
  api.put(`${EVENTS_BASE_URL}/credit-notes/${id}`, payload);

export const deleteEventCreditNote = (id) =>
  api.delete(`${EVENTS_BASE_URL}/credit-notes/${id}`);

export const approveEventCreditNote = (id, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/credit-notes/${id}/approve`, payload);

export const rejectEventCreditNote = (id, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/credit-notes/${id}/reject`, payload);

export const cancelEventCreditNote = (id, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/credit-notes/${id}/cancel`, payload);

export const getEntitySourceDocumentsForCreditNote = (entityType, entityId, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/credit-notes/entity/${entityType}/${entityId}/documents`, { params });

export const getEventCreditNoteStatistics = (params = {}) =>
  api.get(`${EVENTS_BASE_URL}/credit-notes/statistics`, { params });

export const writeOffEventCreditNoteBalance = (payload) =>
  api.post(`${EVENTS_BASE_URL}/credit-notes/write-off-balance`, payload);

export const getEventCreditNotesByInvoice = (invoiceId) =>
  api.get(`${EVENTS_BASE_URL}/credit-notes/invoice/${invoiceId}`);

// Apply (settle) an approved credit note against its source document
export const applyEventCreditNote = (id, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/credit-notes/${id}/apply`, payload);

// Allocate an approved on-account credit note toward a specific contract/invoice
export const allocateEventCreditNote = (id, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/credit-notes/${id}/allocate`, payload);

// Void / reverse an approved or applied credit note (creates GL reversal)
export const voidEventCreditNote = (id, payload = {}) =>
  api.post(`${EVENTS_BASE_URL}/credit-notes/${id}/void`, payload);



// ============================================================================
// EVENT REPORTS (per-report READ / EXPORT permissions enforced server-side)
// ============================================================================

export const getEventReportCatalog = () => api.get(`${EVENTS_BASE_URL}/reports/catalog`);
export const getEventReportOptions = () => api.get(`${EVENTS_BASE_URL}/reports/options`);
export const getEventReportMeta = (reportKey) => api.get(`${EVENTS_BASE_URL}/reports/${reportKey}/meta`);
export const getEventReportData = (reportKey, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/reports/${reportKey}`, { params, ...eventHeavyRequest });
export const exportEventReport = (reportKey, format, params = {}) =>
  api.get(`${EVENTS_BASE_URL}/reports/${reportKey}/export/${format}`, {
    params,
    responseType: 'blob',
    timeout: 180000,
    validateStatus: () => true,
  });

// Direct-assignment rounds: applicants x judges matrix + staff re-enable (no panels)
export const getDirectRoundScoreMatrix = (eventId, roundId) =>
  api.get(`${EVENTS_BASE_URL}/judge-scores/${eventId}/rounds/${roundId}/score-matrix`);
export const reenableDirectRoundScoring = (eventId, roundId, payload) =>
  api.post(`${EVENTS_BASE_URL}/judge-scores/${eventId}/rounds/${roundId}/reenable-scoring`, payload);
